import { PORTAL_ROOT } from './constants';

/**
 * Utility functions for evaluating contact information conditions
 * Supports complex AND/OR logic with pattern matching for any contact property
 * (webRoleNames, customerTypeCode, accountType, etc.)
 */

/**
 * Evaluates an IS condition for any contact property
 *
 * @param condition - The IS condition
 * @param contactInfo - The contact information object
 * @returns True if the condition is met
 */
export const evaluateIsCondition = (
  condition: string,
  contactInfo: Record<string, any>,
): boolean => {
  // Extract property name and pattern from condition (e.g., "IS webRoleNames:/Support*/")
  const match = condition.match(/IS (\w+):([^)]+)/);
  if (!match) {
    console.warn(`Invalid IS condition format: ${condition}`);
    return false;
  }

  const [, propertyName, pattern] = match;
  const propertyValue = contactInfo[propertyName];

  if (propertyValue === undefined || propertyValue === null) {
    return false;
  }

  try {
    // Check if pattern is a regex pattern (starts and ends with /)
    if (pattern.startsWith('/') && pattern.endsWith('/')) {
      // Regex pattern matching
      const cleanPattern = pattern.replace(/^\/+|\/+$/g, '');
      const regexPattern = cleanPattern.replace(/\*/g, '.*');
      const regex = new RegExp(`^${regexPattern}$`);

      // Handle different property types
      if (Array.isArray(propertyValue)) {
        // For arrays (like webRoleNames), check if any element matches
        return propertyValue.some((item: any) => regex.test(String(item)));
      } else {
        // For single values, check if the value matches
        return regex.test(String(propertyValue));
      }
    } else {
      // Exact match for non-regex patterns
      if (Array.isArray(propertyValue)) {
        // For arrays, check if any element matches exactly
        return propertyValue.some((item: any) => String(item) === pattern);
      } else {
        // For single values, check exact match
        return String(propertyValue) === pattern;
      }
    }
  } catch (error) {
    console.error(`Error evaluating condition: ${condition}`, error);
    return false;
  }
};

/**
 * Evaluates a NOT condition for any contact property
 *
 * @param condition - The NOT condition
 * @param contactInfo - The contact information object
 * @returns True if the condition is met (no matching values found)
 */
export const evaluateNotCondition = (
  condition: string,
  contactInfo: Record<string, any>,
): boolean => {
  // Extract property name and pattern from condition (e.g., "NOT webRoleNames:/Partner*/")
  const match = condition.match(/NOT (\w+):([^)]+)/);
  if (!match) {
    console.warn(`Invalid NOT condition format: ${condition}`);
    return false;
  }

  const [, propertyName, pattern] = match;
  const propertyValue = contactInfo[propertyName];

  if (propertyValue === undefined || propertyValue === null) {
    return true; // NOT condition is satisfied if property doesn't exist
  }

  try {
    // Check if pattern is a regex pattern (starts and ends with /)
    if (pattern.startsWith('/') && pattern.endsWith('/')) {
      // Regex pattern matching
      const cleanPattern = pattern.replace(/^\/+|\/+$/g, '');
      const regexPattern = cleanPattern.replace(/\*/g, '.*');
      const regex = new RegExp(`^${regexPattern}$`);

      // Handle different property types
      if (Array.isArray(propertyValue)) {
        // For arrays (like webRoleNames), check if any element matches (NOT means none should match)
        return !propertyValue.some((item: any) => regex.test(String(item)));
      } else {
        // For single values, check if the value matches (NOT means it shouldn't match)
        return !regex.test(String(propertyValue));
      }
    } else {
      // Exact match for non-regex patterns
      if (Array.isArray(propertyValue)) {
        // For arrays, check if any element matches exactly (NOT means none should match)
        return !propertyValue.some((item: any) => String(item) === pattern);
      } else {
        // For single values, check exact match (NOT means it shouldn't match)
        return String(propertyValue) !== pattern;
      }
    }
  } catch (error) {
    console.error(`Error evaluating condition: ${condition}`, error);
    return false;
  }
};

/**
 * Evaluates a contact information condition string dynamically
 *
 * @param condition - The contact information condition string
 * @param contactInfo - The contact information object containing all properties
 * @returns True if the condition is met
 */
export const evaluateContactInformationCondition = (
  condition: string,
  contactInfo: Record<string, any>,
): boolean => {
  try {
    // Handle OR conditions first (higher precedence)
    if (condition.includes(' OR ')) {
      const orConditions = condition.split(' OR ').map((c) => c.trim());
      return orConditions.some((orCondition) =>
        evaluateContactInformationCondition(orCondition, contactInfo),
      );
    }

    // Handle AND conditions
    if (condition.includes(' AND ')) {
      const andConditions = condition.split(' AND ').map((c) => c.trim());
      return andConditions.every((andCondition) =>
        evaluateContactInformationCondition(andCondition, contactInfo),
      );
    }

    // Handle individual conditions
    const trimmedCondition = condition.trim();

    if (trimmedCondition.startsWith('IS ')) {
      return evaluateIsCondition(trimmedCondition, contactInfo);
    } else if (trimmedCondition.startsWith('NOT ')) {
      return evaluateNotCondition(trimmedCondition, contactInfo);
    }

    // If no recognized condition, return false
    console.warn(`Unknown contact information condition format: ${condition}`);
    return false;
  } catch (error) {
    console.error(`Error evaluating contact information condition: ${condition}`, error);
    return false;
  }
};

/**
 * Determines if a user should be redirected to a specific path based on contact information conditions
 *
 * @param currentPath - The current path being requested
 * @param contactInformationCondition - The contact information condition string
 * @param contactInfo - The contact information object containing all properties
 * @param portalRoot - The portal root path (default: '/portal')
 * @returns True if the user should be redirected
 */
export const shouldRedirectToPath = (
  currentPath: string,
  contactInformationCondition: string,
  contactInfo: Record<string, any>,
  portalRoot: string = PORTAL_ROOT,
): boolean => {
  // Only redirect if the current path is the portal root
  if (currentPath !== portalRoot) {
    return false;
  }

  // Parse and evaluate the contact information condition
  return evaluateContactInformationCondition(contactInformationCondition, contactInfo);
};
